import { useState, useEffect, FormEvent } from 'react';
import { useAuth } from '../context/AuthContext';
import AppLayout from '../components/Layout/AppLayout';
import { api } from '../services/api';
import type { PrequalQuestion, PrequalResult } from '@vedjegy/shared';

// Answer map: questionId -> selectedOptionId
type AnswerMap = Record<string, string>;

type Phase = 'loading' | 'privacy' | 'intro' | 'questions' | 'submitting' | 'result' | 'ineligible';

interface OnlineUrls {
  website:   string;
  facebook:  string;
  instagram: string;
  youtube:   string;
  linkedin:  string;
  other:     string;
}

const EMPTY_URLS: OnlineUrls = {
  website: '', facebook: '', instagram: '',
  youtube: '', linkedin: '', other: '',
};

const POSITIONS = [
  'CEO / ügyvezető / cégvezető',
  'Pénzügyi igazgató / vezető',
  'Kereskedelmi igazgató / vezető',
  'Marketing igazgató / vezető',
  'Irodavezető',
  'Könyvelő',
  'Egyéb',
];

const TAGOZAT_OPTIONS = [
  'Szolgáltatási tagozat',
  'Ipari tagozat',
  'Kereskedelmi tagozat',
  'Kézműipari tagozat',
];

const SIZE_LABELS: Record<string, string> = {
  mikro:   'Mikrovállalkozás (0–9 fő)',
  kis:     'Kisvállalkozás (10–49 fő)',
  kozepes: 'Középvállalkozás (50–249 fő)',
  nagy:    'Nagyvállalat (250+ fő)',
};

function deriveSizeCategory(count: string): string {
  const n = parseInt(count, 10);
  if (isNaN(n) || count === '') return '';
  if (n <= 9)   return 'mikro';
  if (n <= 49)  return 'kis';
  if (n <= 249) return 'kozepes';
  return 'nagy';
}

const PRIVACY_TEXT = `
<b>Adatkezelő:</b> Győr-Moson-Sopron Vármegyei Kereskedelmi és Iparkamara,<br>
Adószám: 18529186-2-08<br><br>

<b>1) A „Kamara által ajánlott vállalkozás” védjegyprogram előminősítési és pályázat folyamata során keletkező személyes adatok kezelése</b><br><br>

A védjegyprogram előminősítése és a pályázat folyamata során a vállalkozás adatain túl, a kitöltő személyes adatait is kezeljük. Illetve egyéni vállalkozó esetén a vállalkozás adatai nem különülnek el a természetes személy adataitól. Az adatkezelés adatkezelőre ruházott közhatalmi jogosítvány gyakorlásának keretében végzett feladat végrehajtásához szükséges. [Az adatkezelés jogalapja GDPR 6) e)].<br><br>

Ön bármikor tiltakozhat az adatok kezelése ellen. Ehhez kérjük írásban vegye fel velünk a kapcsolatot!<br><br>

<b>Kezelt adatok köre:</b> Adószám, vállalkozás neve, e-mail cím, kitöltő neve, telefonszám, beosztása, a felmérés kitöltésének ténye, időpontja, a felmérés során megadott egyéb személyes adatok, vagy a megadott válaszokból levonható következtetések, meghívott munkatársak e-mail címe, egyéni vállalkozó esetén, amennyiben a vállalkozás címe megegyezik a vállalkozó lakcímével: lakcím, levelezési cím.<br><br>

<b>Adatok tárolása és megőrzése:</b> Az adatokat az előminősítési és a teljes pályázati folyamat lezárása után 5 évig megőrzi.<br><br>

<b>Adattovábbítások:</b> Az adatok a honlap technológiai szolgáltatójának szerverein kerülnek tárolásra.<br><br>

<b>2) Honlap üzemeltetés és cookie elhelyezések</b><br><br>

A honlaplátogatás során, a szerver naplókban személyes adatokat rögzítünk kiberbiztonsági célokból. Ez az adatkezelés az adatkezelőre ruházott közhatalmi jogosítvány gyakorlásának keretében végzett feladat végrehajtásához szükséges. [Az adatkezelés jogalapja GDPR 6) e)].<br><br>

<b>Kezelt adatok köre:</b> Látogatók IP címe, Böngésző „ujjlenyomata”<br><br>

<b>Adattovábbítások:</b> Az adatok a honlap technológiai szolgáltatójának szerverein kerülnek tárolásra.<br><br>

<b>Tárolási idő:</b> 1 hónap<br><br>

A honlaptámogatás során a honlap munkameneti cookie-kat helyez el. [Az adatkezelés jogalapja GDPR 6) e)]<br><br>

<b>Kezelt adatok köre:</b> Látogatók IP címe, A weboldal látogatás ténye<br><br>

<b>Adattovábbítások:</b> Az adatokat a honlap technológiai szolgáltatójával megosztjuk.<br><br>

<b>Tárolási idő:</b> Az adatok a böngészési előzmények törléséig maradnak elérhetőek, melyet ön szabályoz.<br><br>

<b>Az érintett jogai</b><br><br>

Érintettként kérelmezheti az adatkezelőtől az Önre vonatkozó személyes adatokhoz való hozzáférést, azok helyesbítését, törlését vagy kezelésének korlátozását, és tiltakozhat az ilyen személyes adatok kezelése ellen, valamint rendelkezhet az adathordozhatósághoz való jogáról.<br><br>

<b>Kapcsolatfelvétel:</b> <a href=”mailto:adatvedelem@gymsmkik.hu”>adatvedelem@gymsmkik.hu</a><br><br>

<b>Teljes adatkezelési tájékoztató:</b> <a href=”https://gymsmkik.hu/adatvedelmi-szabalyzatok” target=”_blank” rel=”noopener noreferrer”>https://gymsmkik.hu/adatvedelmi-szabalyzatok</a>`;

function isValidHungarianPhone(v: string): boolean {
  // Strip separators (spaces, dashes, dots, parens), then check for +36 + 8 or 9 digits.
  // Covers mobile (+36 30/20/70 XXX XXXX = 9 digits), Budapest (+36 1 XXX XXXX = 8 digits),
  // and regional landlines (+36 96 XXX XXX = 8 digits).
  const normalized = v.trim().replace(/[\s\-().]/g, '');
  return /^\+36\d{8,9}$/.test(normalized);
}


// Per-platform URL format validation
// Returns an error string or '' if valid (empty field is always OK)
function validateUrlFormat(key: keyof OnlineUrls, value: string): string {
  if (!value.trim()) return '';
  let parsed: URL;
  try {
    parsed = new URL(value.trim());
  } catch {
    return 'Érvénytelen URL formátum. Példa: https://ceged.hu';
  }
  if (!['http:', 'https:'].includes(parsed.protocol)) {
    return 'Csak http:// vagy https:// URL elfogadott.';
  }
  const h = parsed.hostname.replace(/^www\./, '');
  switch (key) {
    case 'facebook':
      if (!['facebook.com', 'fb.com', 'm.facebook.com'].includes(h))
        return 'Facebook URL-nek facebook.com domainnel kell kezdődnie.';
      break;
    case 'instagram':
      if (h !== 'instagram.com')
        return 'Instagram URL-nek instagram.com domainnel kell kezdődnie.';
      break;
    case 'youtube':
      if (!['youtube.com', 'youtu.be'].includes(h))
        return 'YouTube URL-nek youtube.com vagy youtu.be domainnel kell kezdődnie.';
      break;
    case 'linkedin':
      if (h !== 'linkedin.com')
        return 'LinkedIn URL-nek linkedin.com domainnel kell kezdődnie.';
      break;
    default:
      break;
  }
  return '';
}

// Returns true if the question is the online presence question
function isOnlinePresenceQuestion(q: PrequalQuestion): boolean {
  return q.text.toLowerCase().includes('online platform');
}

// Returns the option id that means "yes" (online presence exists)
function getYesOptionId(q: PrequalQuestion): string | undefined {
  const byText = q.options.find((o) => o.text.toLowerCase().includes('igen'));
  if (byText) return byText.id;
  // Fallback: highest-score option
  const sorted = [...q.options].sort((a, b) => b.score - a.score);
  return sorted[0]?.id;
}

// Fire-and-forget draft save -- never throws
async function saveDraftSilent(
  hasEmployees: boolean,
  answers: AnswerMap,
  fillerName?: string,
  fillerPhone?: string,
  position?: string,
): Promise<void> {
  try {
    await api.prequalification.saveDraft({
      hasEmployees,
      answers: Object.entries(answers).map(([questionId, selectedOptionId]) => ({
        questionId,
        selectedOptionId,
      })),
      fillerName,
      fillerPhone,
      position,
    });
  } catch {
    // Silently ignore -- draft save failures are non-critical
  }
}

// Fire-and-forget URL save to company profile -- never throws
async function saveUrlsSilent(urls: OnlineUrls): Promise<void> {
  const hasAny = Object.values(urls).some((v) => typeof v === 'string' && v.trim() !== '');
  if (!hasAny) return;
  try {
    await api.profile.update({
      websiteUrl:          urls.website   || null,
      facebookUrl:         urls.facebook  || null,
      instagramUrl:        urls.instagram || null,
      youtubeUrl:          urls.youtube   || null,
      linkedinUrl:         urls.linkedin  || null,
      otherOnlinePresence: urls.other     || null,
    });
  } catch {
    // Silently ignore
  }
}

export default function PrequalificationPage() {
  const { refresh } = useAuth();
  const [phase, setPhase] = useState<Phase>('loading');

  // Intro
  const [companyTaxId, setCompanyTaxId]   = useState('');
  const [companyName, setCompanyName]     = useState('');
  const [hasEmployees, setHasEmployees]   = useState(true);
  const [fillerName, setFillerName]       = useState('');
  const [fillerPhone, setFillerPhone]     = useState('');
  const [position, setPosition]           = useState('');
  const [positionOther, setPositionOther] = useState('');
  const [chapter, setChapter]                                   = useState('');
  const [employeeCount, setEmployeeCount]                       = useState('');
  const [employeeCountFromProfile, setEmployeeCountFromProfile] = useState(false);
  const [introErrors, setIntroErrors]                           = useState<Record<string, string>>({});

  // Saved draft answers (restored before questions are loaded)
  const [draftAnswers, setDraftAnswers] = useState<AnswerMap>({});

  // Online presence URLs (stored in company record)
  const [onlineUrls, setOnlineUrls] = useState<OnlineUrls>(EMPTY_URLS);

  // Questions -- loaded from API
  const [questions, setQuestions]               = useState<PrequalQuestion[]>([]);
  const [passThreshold, setPassThreshold]       = useState(0.7);
  const [questionsLoading, setQuestionsLoading] = useState(false);
  const [questionsError, setQuestionsError]     = useState('');

  const [currentIndex, setCurrentIndex] = useState(0);
  const [answers, setAnswers]           = useState<AnswerMap>({});

  // Result
  const [result, setResult]            = useState<PrequalResult | null>(null);
  const [renewalBypass, setRenewalBypass] = useState(false);
  const [minMemberSince, setMinMemberSince] = useState<string | null>(null);
  const [submitError, setSubmitError] = useState('');
  // Tracks whether the low-score confirmation banner is shown before submit
  const [confirmSubmit, setConfirmSubmit] = useState(false);
  const [urlErrors,   setUrlErrors]   = useState<Record<keyof OnlineUrls, string>>({ website: '', facebook: '', instagram: '', youtube: '', linkedin: '', other: '' });
  const [urlWarnings, setUrlWarnings] = useState<Record<keyof OnlineUrls, string>>({ website: '', facebook: '', instagram: '', youtube: '', linkedin: '', other: '' });
  const [urlChecking, setUrlChecking] = useState<Record<keyof OnlineUrls, boolean>>({ website: false, facebook: false, instagram: false, youtube: false, linkedin: false, other: false });
  // Error shown when "Igen" is selected but no URL has been provided
  const [urlRequiredError, setUrlRequiredError] = useState('');

  // On mount: load company profile + draft in parallel, decide starting phase
  useEffect(() => {
    Promise.all([
      api.profile.get().catch(() => null),
      api.prequalification.loadDraft().catch(() => null),
    ]).then(([profile, draft]) => {
      // Restore company data
      if (profile?.company) {
        setCompanyTaxId(profile.company.taxId ?? '');
        setCompanyName(profile.company.name ?? '');
        const count = profile.company.employeeCount ?? 0;
        setHasEmployees(count > 0);
        if (profile.company.employeeCount != null) {
          setEmployeeCount(String(profile.company.employeeCount));
          setEmployeeCountFromProfile(true);
        }
        setChapter(profile.company.chapter ?? '');
        // Restore online presence URLs
        setOnlineUrls({
          website:   profile.company.websiteUrl   ?? '',
          facebook:  profile.company.facebookUrl  ?? '',
          instagram: profile.company.instagramUrl ?? '',
          youtube:   profile.company.youtubeUrl   ?? '',
          linkedin:  profile.company.linkedinUrl  ?? '',
          other:     '',  // otherOnlinePresence is JSONB array; not editable here
        });
      }

      // Restore draft if present
      if (draft) {
        // Membership date ineligibility — show blocking message
        if (draft.membershipIneligible) {
          setMinMemberSince(draft.minMemberSince ?? null);
          setPhase('ineligible');
          return;
        }

        // If already submitted this year, jump straight to the result screen
        if (draft.alreadySubmitted && draft.result) {
          setResult(draft.result);
          if (draft.renewalBypass) setRenewalBypass(true);
          setPhase('result');
          return;
        }

        if (draft.fillerName)  setFillerName(draft.fillerName);
        if (draft.fillerPhone) setFillerPhone(draft.fillerPhone);
        if (draft.position)    setPosition(draft.position);
        if (draft.hasEmployees !== null) setHasEmployees(draft.hasEmployees);
        if (draft.answers.length > 0) {
          const restored: AnswerMap = {};
          for (const a of draft.answers) restored[a.questionId] = a.selectedOptionId;
          setDraftAnswers(restored);
        }

        // Determine starting phase from what was saved
        if (draft.fillerName) {
          setPhase('questions');
        } else if (draft.answers.length > 0 || draft.hasEmployees !== null) {
          setPhase('intro');
        } else {
          setPhase('privacy');
        }
      } else {
        setPhase('privacy');
      }
    });
  }, []);

  // Fetch questions when entering question phase
  useEffect(() => {
    if (phase !== 'questions') return;
    setQuestionsLoading(true);
    setQuestionsError('');
    api.prequalification.listQuestions()
      .then((questionsData) => {
        setQuestions(questionsData.questions);
        setPassThreshold(questionsData.config.passThreshold);
      })
      .catch(() => setQuestionsError('Nem sikerült betölteni a kérdéseket. Kérjük, próbáljon újra.'))
      .finally(() => setQuestionsLoading(false));
  }, [phase]);

  // Restore draft answers + jump to first unanswered question once both
  // questions and draftAnswers are available. Kept separate from the questions
  // fetch effect so there is no stale-closure risk on draftAnswers.
  useEffect(() => {
    if (questions.length === 0) return;
    if (Object.keys(draftAnswers).length === 0) return;
    setAnswers(draftAnswers);
    const active = questions.filter((q) => !q.skipWhenNoEmployees || hasEmployees);
    const firstUnanswered = active.findIndex((q) => !draftAnswers[q.id]);
    setCurrentIndex(firstUnanswered >= 0 ? firstUnanswered : active.length - 1);
  }, [questions, draftAnswers, hasEmployees]);

  // Active questions (skip employee-only questions when no employees)
  const activeQuestions = questions.filter(
    (q) => !q.skipWhenNoEmployees || hasEmployees
  );

  // Running score
  const currentScore = activeQuestions.reduce((sum, q) => {
    const selId = answers[q.id];
    if (!selId) return sum;
    const opt = q.options.find((o) => o.id === selId);
    return sum + (opt?.score ?? 0);
  }, 0);

  const maxScore = activeQuestions.reduce((sum, q) => {
    const maxOpt = Math.max(0, ...q.options.map((o) => o.score));
    return sum + maxOpt;
  }, 0);

  // Points needed to pass; derived from API-provided passThreshold
  const requiredScore    = maxScore > 0 ? Math.round(passThreshold * maxScore) : 0;
  const thresholdPct     = Math.round(passThreshold * 100);
  const isBelowThreshold = maxScore > 0 && currentScore < requiredScore;

  const answeredCount    = activeQuestions.filter((q) => answers[q.id]).length;
  const totalQuestions   = activeQuestions.length;
  const q                = activeQuestions[currentIndex];
  const selectedOptionId = q ? answers[q.id] : undefined;
  const progressPct      = totalQuestions > 0
    ? Math.round(((currentIndex + 1) / totalQuestions) * 100)
    : 0;

  // Online presence sub-form visibility
  const isOnlineQ     = q ? isOnlinePresenceQuestion(q) : false;
  const yesOptionId   = q && isOnlineQ ? getYesOptionId(q) : undefined;
  const showUrlFields = isOnlineQ && selectedOptionId === yesOptionId;

  // Handlers

  function handlePrivacyNext(e: FormEvent) {
    e.preventDefault();
    void saveDraftSilent(hasEmployees, draftAnswers);
    setPhase('intro');
  }

  function handleIntroNext(e: FormEvent) {
    e.preventDefault();
    const errs: Record<string, string> = {};
    if (!fillerName.trim())                        errs.fillerName  = 'A kitöltő neve kötelező.';
    if (!fillerPhone.trim())                       errs.fillerPhone = 'A telefonszám megadása kötelező.';
    else if (!isValidHungarianPhone(fillerPhone))  errs.fillerPhone = 'Érvénytelen formátum. Példa: +36 30 123 4567 vagy +36 1 234 5678';
    if (!position)                                 errs.position    = 'A beosztás megadása kötelező.';
    if (position === 'Egyéb' && !positionOther.trim()) errs.positionOther = 'Kérjük, adja meg a beosztását.';
    if (!employeeCount.trim())                     errs.employeeCount = 'A foglalkoztatottak száma kötelező.';
    if (Object.keys(errs).length) { setIntroErrors(errs); return; }
    setIntroErrors({});
    // Derive hasEmployees from the entered count
    const empNum = parseInt(employeeCount, 10);
    const newHasEmployees = !isNaN(empNum) && empNum > 0;
    setHasEmployees(newHasEmployees);
    void saveDraftSilent(newHasEmployees, draftAnswers, fillerName, fillerPhone,
      position === 'Egyéb' ? positionOther : position);
    // Save company fields to profile if user entered them here
    const profilePatch: Record<string, unknown> = {};
    if (chapter) profilePatch.chapter = chapter;
    if (!employeeCountFromProfile && !isNaN(empNum)) {
      profilePatch.employeeCount = empNum;
      profilePatch.sizeCategory  = deriveSizeCategory(employeeCount) || null;
    }
    if (Object.keys(profilePatch).length) {
      void api.profile.update(profilePatch as Parameters<typeof api.profile.update>[0]);
    }
    setPhase('questions');
  }

  async function handleUrlBlur(key: keyof OnlineUrls, value: string) {
    const fmtErr = validateUrlFormat(key, value);
    setUrlErrors((prev) => ({ ...prev, [key]: fmtErr }));
    // Clear stale warning when field changes
    setUrlWarnings((prev) => ({ ...prev, [key]: '' }));
    if (fmtErr || !value.trim()) return;
    // Async reachability check — non-blocking warning
    setUrlChecking((prev) => ({ ...prev, [key]: true }));
    try {
      const { reachable } = await api.profile.checkUrl(value.trim());
      setUrlWarnings((prev) => ({
        ...prev,
        [key]: reachable ? '' : 'A megadott URL nem elérhető. Kérjük, ellenőrizze a címet.',
      }));
    } catch {
      // Network error during check — don't show a false warning
    } finally {
      setUrlChecking((prev) => ({ ...prev, [key]: false }));
    }
  }

  function handleSelect(optionId: string) {
    if (!q) return;
    const newAnswers = { ...answers, [q.id]: optionId };
    setAnswers(newAnswers);
    // Autosave on selection so a refresh before clicking Next doesn't lose the answer
    void saveDraftSilent(hasEmployees, newAnswers, fillerName, fillerPhone,
      position === 'Egyéb' ? positionOther : position);
    // If deselecting "yes" on online presence question, save URLs too
    if (isOnlineQ && optionId !== yesOptionId) {
      void saveUrlsSilent(onlineUrls);
    }
  }

  function handlePrev() {
    if (currentIndex > 0) {
      void saveDraftSilent(hasEmployees, answers, fillerName, fillerPhone,
        position === 'Egyéb' ? positionOther : position);
      if (showUrlFields) void saveUrlsSilent(onlineUrls);
      setCurrentIndex((i) => i - 1);
    }
  }

  function handleNext() {
    if (currentIndex < totalQuestions - 1) {
      // When "Igen" selected for online presence, at least one URL is required
      if (showUrlFields && !Object.values(onlineUrls).some((v) => v.trim() !== '')) {
        setUrlRequiredError('Kérjük, adjon meg legalább egy érvényes online elérhetőséget (weboldal, közösségi média stb.).');
        return;
      }
      void saveDraftSilent(hasEmployees, answers, fillerName, fillerPhone,
        position === 'Egyéb' ? positionOther : position);
      if (showUrlFields) void saveUrlsSilent(onlineUrls);
      setCurrentIndex((i) => i + 1);
    }
  }

  async function handleSubmit() {
    setPhase('submitting');
    setSubmitError('');
    try {
      // Save online presence URLs to company profile before submitting
      await saveUrlsSilent(onlineUrls);

      const answersPayload = activeQuestions.map((aqq) => ({
        questionId: aqq.id,
        selectedOptionId: answers[aqq.id] ?? null,
        isApplicable: true,
      }));

      const skippedPayload = questions
        .filter((sq) => sq.skipWhenNoEmployees && !hasEmployees)
        .map((sq) => ({
          questionId: sq.id,
          selectedOptionId: null,
          isApplicable: false,
        }));

      const res = await api.prequalification.submit({
        hasEmployees,
        answers: [...answersPayload, ...skippedPayload],
        fillerName,
        fillerPhone,
        position: position === 'Egyéb' ? positionOther : position,
      });
      setResult(res);
      await refresh();
      setPhase('result');
    } catch (err) {
      setSubmitError(err instanceof Error ? err.message : 'Szerverhiba.');
      setPhase('questions');
    }
  }

  // Membership ineligibility phase
  if (phase === 'ineligible') {
    const formattedDate = minMemberSince
      ? new Date(minMemberSince).toLocaleDateString('hu-HU', { year: 'numeric', month: 'long', day: 'numeric' })
      : null;
    return (
      <AppLayout>
        <div style={{ padding: '40px', maxWidth: 640 }}>
          <div style={{
            display: 'flex', gap: 14, alignItems: 'flex-start',
            background: 'rgba(0,48,46,.05)', border: '1px solid rgba(0,48,46,.2)',
            borderRadius: 10, padding: '20px 24px',
          }}>
            <span style={{ fontSize: 22, lineHeight: 1, marginTop: 2 }}>ℹ️</span>
            <div>
              <div style={{ fontWeight: 700, fontSize: 15, color: 'var(--primary)', marginBottom: 6 }}>
                Az előminősítés jelenleg nem elérhető
              </div>
              <p style={{ margin: 0, fontSize: 14, color: 'var(--primary)', lineHeight: 1.6 }}>
                Jelen pályázási időszakban azon tagok pályázhatnak, akik {formattedDate ? ` ${formattedDate}-ig` : ' megadott dátumig'} létesítettek önkéntes kamarai tagviszonyt.
              </p>
              <p style={{ margin: '10px 0 0', fontSize: 13, color: 'var(--muted-foreground)' }}>
                Amennyiben kérdése van, vegye fel a kapcsolatot a kamarával.
              </p>
            </div>
          </div>
        </div>
      </AppLayout>
    );
  }

  // Loading phase
  if (phase === 'loading') {
    return (
      <AppLayout>
        <div style={{ padding: '40px', color: 'var(--muted-foreground)' }}>Betöltés…</div>
      </AppLayout>
    );
  }

  // Privacy phase
  if (phase === 'privacy') {
    return (
      <AppLayout>
        <div style={{ padding: '32px 40px', maxWidth: 2000 }}>
          <div style={{ marginBottom: 24 }}>
            <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: '.12em', textTransform: 'uppercase', color: 'var(--secondary-foreground)', marginBottom: 6 }}>
              ELŐMINŐSÍTÉS · 1. LÉPÉS
            </div>
            <h1 style={{ margin: 0, fontSize: 26, fontWeight: 700, color: 'var(--primary)' }}>Adatvédelmi tájékoztató</h1>
          </div>
          <div style={{ background: '#fff', border: '1px solid var(--border)', borderRadius: 'var(--radius-lg)', padding: '32px 36px', boxShadow: 'var(--shadow-md)', marginBottom: 24 }}>
            <div style={{ fontSize: 14, lineHeight: 1.6, color: 'var(--primary)' }} dangerouslySetInnerHTML={{ __html: PRIVACY_TEXT }} />
          </div>
          <form onSubmit={handlePrivacyNext}>
            <button type="submit" className="btn btn-primary">Tovább →</button>
          </form>
        </div>
      </AppLayout>
    );
  }

  // Intro phase
  if (phase === 'intro') {
    return (
      <AppLayout>
        <div style={{ padding: '32px 40px', maxWidth: 2000 }}>
          <div style={{ marginBottom: 24 }}>
            <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: '.12em', textTransform: 'uppercase', color: 'var(--secondary-foreground)', marginBottom: 6 }}>
              ELŐMINŐSÍTÉS · 2. LÉPÉS
            </div>
            <h1 style={{ margin: 0, fontSize: 26, fontWeight: 700, color: 'var(--primary)' }}>Azonosító adatok</h1>
            <p style={{ margin: '6px 0 0', fontSize: 14, color: 'var(--muted-foreground)' }}>
              Kérjük, adja meg a kitöltő adatait. A vállalkozás adatai automatikusan kerültek kitöltésre.
            </p>
          </div>
          <div style={{ background: '#fff', border: '1px solid var(--border)', borderRadius: 'var(--radius-lg)', padding: '32px 36px', boxShadow: 'var(--shadow-md)' }}>
            <form onSubmit={handleIntroNext} noValidate>
              <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: '.1em', textTransform: 'uppercase', color: 'var(--secondary-foreground)', marginBottom: 14, paddingBottom: 8, borderBottom: '1px solid var(--border)' }}>
                Vállalkozás adatai
              </div>
              <div style={{ display: 'flex', gap: 16, marginBottom: 20 }}>
                <div className="field" style={{ flex: 1, marginBottom: 0 }}>
                  <label>Adószám</label>
                  <input type="text" value={companyTaxId} readOnly style={{ background: 'var(--muted)', cursor: 'default' }} />
                </div>
                <div className="field" style={{ flex: 2, marginBottom: 0 }}>
                  <label>Vállalkozás neve</label>
                  <input type="text" value={companyName} readOnly style={{ background: 'var(--muted)', cursor: 'default' }} />
                </div>
              </div>
              {/* Employee count */}
              <div style={{ display: 'flex', gap: 16, marginBottom: 20 }}>
                <div className="field" style={{ flex: 1, marginBottom: 0 }}>
                  <label>
                    Foglalkoztatottak száma <span style={{ color: 'var(--destructive)' }}>*</span>
                    {employeeCountFromProfile && (
                      <span style={{ marginLeft: 8, fontSize: 11, fontWeight: 700, letterSpacing: '.06em', textTransform: 'uppercase', background: 'var(--secondary)', color: '#fff', borderRadius: 4, padding: '1px 6px' }}>
                        Profil
                      </span>
                    )}
                  </label>
                  <input
                    type="number" min={0}
                    value={employeeCount}
                    readOnly={employeeCountFromProfile}
                    onChange={(e) => {
                      setEmployeeCount(e.target.value);
                      setHasEmployees(parseInt(e.target.value, 10) > 0);
                    }}
                    placeholder="pl. 5"
                    style={{ background: employeeCountFromProfile ? 'var(--muted)' : '#fff', cursor: employeeCountFromProfile ? 'default' : 'text' }}
                  />
                  {introErrors.employeeCount && <div className="error">{introErrors.employeeCount}</div>}
                </div>
                <div className="field" style={{ flex: 2, marginBottom: 0 }}>
                  <label>Méretkategória</label>
                  <input
                    type="text" readOnly
                    value={employeeCount ? (SIZE_LABELS[deriveSizeCategory(employeeCount)] ?? '') : '— adja meg a foglalkoztatottak számát —'}
                    style={{ background: 'var(--muted)', cursor: 'default', color: employeeCount ? 'var(--primary)' : 'var(--muted-foreground)' }}
                  />
                </div>
              </div>
              {/* Tagozat */}
              <div style={{ marginBottom: 16 }}>
                <label style={{ display: 'block', fontSize: 13, fontWeight: 600, color: 'var(--secondary-foreground)', marginBottom: 6 }}>
                  Tagozat <span style={{ color: 'var(--destructive)' }}>*</span>
                </label>
                <select
                  value={chapter}
                  onChange={(e) => setChapter(e.target.value)}
                  disabled={!!chapter}
                  style={{ width: '100%', padding: '10px 14px', fontSize: 14, borderRadius: 'var(--radius-sm)', border: '1px solid var(--border)', background: chapter ? 'var(--muted)' : '#fff', color: 'var(--primary)' }}
                >
                  <option value="">— Válasszon tagozatot —</option>
                  {TAGOZAT_OPTIONS.map((o) => <option key={o} value={o}>{o}</option>)}
                </select>
              </div>
              <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: '.1em', textTransform: 'uppercase', color: 'var(--secondary-foreground)', marginBottom: 14, paddingBottom: 8, borderBottom: '1px solid var(--border)', marginTop: 8 }}>
                Kitöltő adatai
              </div>
              <div className="field">
                <label>Kitöltő neve <span className="req">*</span></label>
                <input type="text" value={fillerName} onChange={(e) => setFillerName(e.target.value)} placeholder="Kovács János" />
                {introErrors.fillerName && <div className="error">{introErrors.fillerName}</div>}
              </div>
              <div className="field">
                <label>Telefonszám <span className="req">*</span></label>
                <input type="tel" value={fillerPhone} onChange={(e) => setFillerPhone(e.target.value)} placeholder="+36 30 123 4567 / +36 1 234 5678" />
                {introErrors.fillerPhone && <div className="error">{introErrors.fillerPhone}</div>}
              </div>
              <div className="field" style={{ marginBottom: position === 'Egyéb' ? 14 : 28 }}>
                <label>Beosztás <span className="req">*</span></label>
                <select value={position} onChange={(e) => { setPosition(e.target.value); setPositionOther(''); }}>
                  <option value="">— válasszon —</option>
                  {POSITIONS.map((p) => <option key={p} value={p}>{p}</option>)}
                </select>
                {introErrors.position && <div className="error">{introErrors.position}</div>}
              </div>
              {position === 'Egyéb' && (
                <div className="field" style={{ marginBottom: 28 }}>
                  <label>Beosztás megnevezése <span className="req">*</span></label>
                  <input type="text" value={positionOther} onChange={(e) => setPositionOther(e.target.value)} placeholder="pl. Értékesítési vezető" />
                  {introErrors.positionOther && <div className="error">{introErrors.positionOther}</div>}
                </div>
              )}
              <div style={{ display: 'flex', gap: 12 }}>
                <button type="button" className="btn btn-ghost" onClick={() => setPhase('privacy')}>← Vissza</button>
                <button type="submit" className="btn btn-primary">Tovább a kérdőívhez →</button>
              </div>
            </form>
          </div>
        </div>
      </AppLayout>
    );
  }

  // Submitting spinner
  if (phase === 'submitting') {
    return (
      <AppLayout>
        <div style={{ padding: '80px 40px', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 16 }}>
          <div style={{ fontSize: 18, fontWeight: 700, color: 'var(--primary)' }}>Beküldés folyamatban…</div>
          <div style={{ color: 'var(--muted-foreground)', fontSize: 14 }}>Kérjük, várjon.</div>
        </div>
      </AppLayout>
    );
  }

  // Result screen
  if (phase === 'result' && result) {
    const pct = result.percentage;
    const resultThresholdPct = Math.round(passThreshold * 100);
    const resultRequiredScore = Math.round(passThreshold * result.maxScore);
    return (
      <AppLayout>
        <div style={{ padding: '32px 40px', maxWidth: 2000 }}>
          <div style={{ marginBottom: 24 }}>
            <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: '.12em', textTransform: 'uppercase', color: 'var(--secondary-foreground)', marginBottom: 6 }}>
              ELŐMINŐSÍTÉS · EREDMÉNY
            </div>
            <h1 style={{ margin: 0, fontSize: 26, fontWeight: 700, color: 'var(--primary)' }}>
              {result.passed ? 'Elkezdheti a pályázat kitöltését!' : 'Az előminősítés nem sikerült'}
            </h1>
          </div>

          {/* Process-closed banner for failed result */}
          {!result.passed && (
            <div className="alert alert-destructive" style={{ marginBottom: 20 }}>
              <span>✗</span>
              <div>
                <strong>A folyamat lezárult</strong>
                <p>
                  Az előminősítés eredménye végleges; a kérdőív ebben az évben nem tölthető ki újra.
                  A következő pályázati évadban lehetőség nyílik az újrapróbálkozásra.
                </p>
              </div>
            </div>
          )}

          <div style={{ background: '#fff', border: '1px solid var(--border)', borderRadius: 'var(--radius-lg)', padding: '36px 40px', boxShadow: 'var(--shadow-md)', marginBottom: 24 }}>
            {renewalBypass ? (
              /* Renewal bypass: no score to display */
              <div className="alert alert-success">
                <span>✓</span>
                <div>
                  <strong>Megújító – előminősítés kihagyva</strong>
                  <p>
                    Vállalkozása megújítóként szerepel a kamarai nyilvántartásban.
                    Az előminősítő kérdőív kitöltése nem szükséges — elkezdheti a pályázat kitöltését!
                  </p>
                </div>
              </div>
            ) : (
              <>
                <div style={{ display: 'flex', alignItems: 'center', gap: 32, marginBottom: 28 }}>
                  <div style={{
                    width: 100, height: 100, borderRadius: '50%', flexShrink: 0,
                    background: result.passed ? 'rgba(78,192,124,.18)' : 'rgba(209,49,46,.10)',
                    border: `4px solid ${result.passed ? 'var(--secondary)' : 'var(--destructive)'}`,
                    display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
                  }}>
                    <span style={{ fontSize: 26, fontWeight: 700, color: result.passed ? '#064935' : '#9f201e', lineHeight: 1 }}>{pct}%</span>
                    <span style={{ fontSize: 11, color: result.passed ? '#064935' : '#9f201e', marginTop: 2 }}>({result.score}/{result.maxScore})</span>
                  </div>
                  <div>
                    <div style={{ fontSize: 16, fontWeight: 700, color: 'var(--primary)', marginBottom: 6 }}>
                      Elért pontszám: <strong>{result.score} / {result.maxScore} pont</strong>
                    </div>
                    <div style={{ fontSize: 14, color: 'var(--muted-foreground)', marginBottom: 4 }}>
                      Megfelelési küszöb:{' '}
                      <strong style={{ color: 'var(--primary)' }}>
                        {resultRequiredScore} / {result.maxScore} pont ({resultThresholdPct}%)
                      </strong>
                    </div>
                    <div style={{ fontSize: 13, color: result.passed ? '#166534' : '#9f201e' }}>
                      {result.passed
                        ? `Eredménye ${pct - resultThresholdPct} százalékponttal haladja meg a küszöböt.`
                        : `Eredménye ${resultThresholdPct - pct} százalékponttal maradt el a küszöbtől.`}
                    </div>
                  </div>
                </div>
                <div className={`alert ${result.passed ? 'alert-success' : 'alert-destructive'}`}>
                  <span>{result.passed ? '✓' : '✗'}</span>
                  <div>
                    <strong>{result.passed ? 'Megfelelt' : 'Nem felelt meg'}</strong>
                    <p>
                      {result.passed
                        ? 'Vállalkozása sikeresen teljesítette az előminősítési feltételeket. Elkezdheti a pályázat kitöltését!'
                        : 'Vállalkozása ebben az évben nem felelte meg az előminősítési feltételeket. Javasoljuk, hogy a jelzett területeken fejlessze tevékenységét, és a következő évben próbálkozzon újra.'}
                    </p>
                  </div>
                </div>
              </>
            )}
          </div>
        </div>
      </AppLayout>
    );
  }

  // Questions phase
  const isLastQuestion = currentIndex === totalQuestions - 1;
  const allAnswered    = answeredCount === totalQuestions;
  const scoreLabel     = maxScore > 0 ? `${currentScore} / ${maxScore} pont` : undefined;

  return (
    <AppLayout sidebarProgress={{ label: 'Előminősítés', current: answeredCount, total: totalQuestions, score: scoreLabel }}>
      <div style={{ padding: '32px 40px' }}>
        {questionsLoading && (
          <div style={{ color: 'var(--muted-foreground)', padding: '40px 0' }}>Kérdések betöltése…</div>
        )}
        {questionsError && (
          <div className="alert alert-destructive" style={{ marginBottom: 24 }}>
            <span>⚠</span><div><p>{questionsError}</p></div>
          </div>
        )}
        {submitError && (
          <div className="alert alert-destructive" style={{ marginBottom: 24 }}>
            <span>⚠</span><div><p>{submitError}</p></div>
          </div>
        )}

        {!questionsLoading && !questionsError && activeQuestions.length > 0 && q && (
          <>
            <div style={{ marginBottom: 22 }}>
              <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: '.12em', textTransform: 'uppercase', color: 'var(--secondary-foreground)', marginBottom: 6 }}>
                ELŐMINŐSÍTÉS · {String(q.questionNumber).padStart(2, '0')}
              </div>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 16, marginBottom: 12 }}>
                <h2 style={{ margin: 0, color: 'var(--primary)', fontSize: 22, fontWeight: 700 }}>
                  {currentIndex + 1}. kérdés
                </h2>
                <span style={{ fontSize: 13, color: 'var(--muted-foreground)', fontWeight: 700 }}>
                  {currentIndex + 1} / {totalQuestions}
                </span>
              </div>
              <div className="progress-track" style={{ marginBottom: 10 }}>
                <div className="progress-fill" style={{ width: `${progressPct}%` }} />
              </div>
              <div style={{ display: 'flex', gap: 5, alignItems: 'center', flexWrap: 'wrap' }}>
                {activeQuestions.map((ques, idx) => {
                  const isAnswered = !!answers[ques.id];
                  const isCurrent  = idx === currentIndex;
                  const bg         = isCurrent ? 'var(--primary)' : isAnswered ? 'var(--secondary)' : 'var(--muted)';
                  const border     = isCurrent || isAnswered ? 'none' : '1px solid var(--border)';
                  return (
                    <div key={ques.id} onClick={() => setCurrentIndex(idx)} style={{
                      width: isCurrent ? 14 : 10, height: isCurrent ? 14 : 10,
                      borderRadius: '50%', background: bg, border,
                      cursor: 'pointer', flexShrink: 0,
                      boxShadow: isCurrent ? '0 0 0 3px rgba(0,48,46,.18)' : undefined,
                      transition: 'all .15s',
                    }} />
                  );
                })}
              </div>

              {/* Pass threshold — always visible during the quiz */}
              {maxScore > 0 && (
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 10, fontSize: 12 }}>
                  <span style={{ color: 'var(--muted-foreground)' }}>
                    Sikerességhez szükséges:{' '}
                    <strong style={{ color: 'var(--primary)' }}>{requiredScore} / {maxScore} pont ({thresholdPct}%)</strong>
                  </span>
                  {answeredCount > 0 && (
                    <span style={{ fontWeight: 700, color: isBelowThreshold ? '#b45309' : '#166534' }}>
                      Jelenlegi: {currentScore} / {maxScore} pt
                    </span>
                  )}
                </div>
              )}
            </div>

            <div style={{ background: '#fff', border: '1px solid var(--border)', borderRadius: 'var(--radius-lg)', padding: '36px 40px', boxShadow: 'var(--shadow-md)', maxWidth: 740 }}>
              <h3 style={{ color: 'var(--primary)', fontSize: 22, fontWeight: 700, margin: '0 0 28px', lineHeight: 1.35 }}>
                {q.text}
              </h3>

              <div style={{ display: 'flex', flexDirection: q.options.length === 2 ? 'row' : 'column', gap: 12, marginBottom: showUrlFields ? 24 : 28 }}>
                {q.options.map((opt) => {
                  const selected = selectedOptionId === opt.id;
                  return (
                    <button key={opt.id} onClick={() => handleSelect(opt.id)} style={{
                      flex: q.options.length === 2 ? 1 : undefined,
                      border: `2px solid ${selected ? 'var(--primary)' : 'var(--border)'}`,
                      borderRadius: 14, padding: '18px 22px',
                      display: 'flex', alignItems: 'center', gap: 14,
                      cursor: 'pointer',
                      background: selected ? 'var(--muted)' : '#fff',
                      fontFamily: 'var(--font-sans)', fontSize: 16, fontWeight: 700,
                      color: selected ? 'var(--primary)' : 'var(--muted-foreground)',
                      textAlign: 'left', transition: 'all .15s',
                    }}>
                      <div style={{
                        width: 22, height: 22, borderRadius: '50%', flexShrink: 0,
                        border: selected ? 'none' : '2px solid var(--border)',
                        background: selected ? 'var(--primary)' : 'transparent',
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                      }}>
                        {selected && (
                          <svg width="11" height="11" viewBox="0 0 12 12" fill="none">
                            <polyline points="2,6 5,9 10,3" stroke="#fff" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"/>
                          </svg>
                        )}
                      </div>
                      {opt.text}
                    </button>
                  );
                })}
              </div>

              {/* Online presence URL sub-form */}
              {showUrlFields && (
                <div style={{
                  borderTop: '1px solid var(--border)',
                  paddingTop: 24, marginBottom: 28,
                }}>
                  <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--primary)', marginBottom: 16 }}>
                    Kérjük, adja meg online elérhetőségeit:
                  </div>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0 20px' }}>
                    {([
                      { key: 'website',   label: 'Weboldal',       placeholder: 'https://ceged.hu' },
                      { key: 'facebook',  label: 'Facebook',        placeholder: 'https://facebook.com/ceged' },
                      { key: 'instagram', label: 'Instagram',       placeholder: 'https://instagram.com/ceged' },
                      { key: 'youtube',   label: 'YouTube',         placeholder: 'https://youtube.com/@ceged' },
                      { key: 'linkedin',  label: 'LinkedIn',        placeholder: 'https://linkedin.com/company/ceged' },
                      { key: 'other',     label: 'Egyéb platform',  placeholder: 'https://...' },
                    ] as { key: keyof OnlineUrls; label: string; placeholder: string }[]).map(({ key, label, placeholder }) => (
                      <div key={key} className="field" style={{ marginBottom: 12 }}>
                        <label style={{ fontSize: 12 }}>{label}</label>
                        <input
                          type="url"
                          value={onlineUrls[key]}
                          onChange={(e) => {
                            setOnlineUrls((prev) => ({ ...prev, [key]: e.target.value }));
                            setUrlErrors((prev) => ({ ...prev, [key]: '' }));
                            setUrlRequiredError('');
                          }}
                          onBlur={(e) => { void handleUrlBlur(key, e.target.value); }}
                          placeholder={placeholder}
                          style={{ fontSize: 13, borderColor: urlErrors[key] ? 'var(--destructive)' : undefined }}
                        />
                        {urlErrors[key] && (
                          <div className="error" style={{ fontSize: 11 }}>{urlErrors[key]}</div>
                        )}
                        {!urlErrors[key] && urlChecking[key] && (
                          <div style={{ fontSize: 11, color: 'var(--muted-foreground)', marginTop: 3 }}>
                            ⏳ Elérhetőség ellenőrzése…
                          </div>
                        )}
                        {!urlErrors[key] && !urlChecking[key] && urlWarnings[key] && (
                          <div style={{ fontSize: 11, color: '#b45309', marginTop: 3 }}>
                            ⚠ {urlWarnings[key]}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* URL required error — shown when "Igen" selected but no link provided */}
              {urlRequiredError && (
                <div className="alert alert-destructive" style={{ marginBottom: 16 }}>
                  <span>⚠</span>
                  <div><p>{urlRequiredError}</p></div>
                </div>
              )}

              {/* Low-score confirmation — shown when submit is attempted below threshold */}
              {confirmSubmit && (
                <div className="alert alert-destructive" style={{ marginBottom: 20 }}>
                  <span>⚠</span>
                  <div>
                    <strong>Figyelem — az eredmény nem éri el a szükséges szintet</strong>
                    <p style={{ marginTop: 4 }}>
                      Jelenlegi pontszáma: <strong>{currentScore} / {maxScore} pont ({maxScore > 0 ? Math.round((currentScore / maxScore) * 100) : 0}%)</strong>.
                      A sikerességhez legalább <strong>{requiredScore} pont ({thresholdPct}%)</strong> szükséges.
                      Ha most beküldi, az előminősítés lezárul és ebben az évben nem ismételhető meg.
                    </p>
                    <div style={{ display: 'flex', gap: 8, marginTop: 12 }}>
                      <button className="btn btn-destructive btn-sm" onClick={() => void handleSubmit()}>
                        Igen, beküldöm
                      </button>
                      <button className="btn btn-ghost btn-sm" onClick={() => setConfirmSubmit(false)}>
                        Vissza
                      </button>
                    </div>
                  </div>
                </div>
              )}
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <button className="btn btn-ghost btn-sm" onClick={handlePrev} disabled={currentIndex === 0}>
                  ← Előző
                </button>
                {isLastQuestion ? (
                  <button
                    className="btn btn-primary"
                    onClick={() => {
                      if (!allAnswered) return;
                      // When "Igen" selected for online presence, at least one URL is required
                      if (showUrlFields && !Object.values(onlineUrls).some((v) => v.trim() !== '')) {
                        setUrlRequiredError('Kérjük, adjon meg legalább egy érvényes online elérhetőséget (weboldal, közösségi média stb.).');
                        return;
                      }
                      // If score is below threshold and not yet confirmed, show the warning
                      if (isBelowThreshold && !confirmSubmit) { setConfirmSubmit(true); return; }
                      void handleSubmit();
                    }}
                    disabled={!allAnswered}
                    title={!allAnswered ? 'Kérjük, válaszoljon minden kérdésre a beküldés előtt.' : undefined}
                  >
                    Beküldés ({answeredCount}/{totalQuestions}) →
                  </button>
                ) : (
                  <button className="btn btn-primary" onClick={handleNext}>
                    Következő →
                  </button>
                )}
              </div>
            </div>

            {answeredCount > 0 && (
              <div style={{ marginTop: 22, maxWidth: 740 }}>
                <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: '.09em', textTransform: 'uppercase', color: 'var(--muted-foreground)', marginBottom: 10 }}>
                  Korábbi válaszok
                </div>
                <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                  {activeQuestions.filter((ques, idx) => idx !== currentIndex && answers[ques.id]).map((ques) => {
                    const selId = answers[ques.id];
                    const opt   = ques.options.find((o) => o.id === selId);
                    const label = opt?.text ?? '—';
                    const short = label.length > 22 ? label.slice(0, 22) + '…' : label;
                    return (
                      <div key={ques.id} onClick={() => setCurrentIndex(activeQuestions.indexOf(ques))} style={{
                        background: '#fff', border: '1px solid var(--border)',
                        borderRadius: 'var(--radius-sm)', padding: '7px 12px',
                        display: 'flex', alignItems: 'center', gap: 7, cursor: 'pointer',
                      }}>
                        <span style={{ fontSize: 12, color: 'var(--muted-foreground)' }}>{ques.questionNumber}.</span>
                        <span style={{ fontSize: 13, fontWeight: 700, color: 'var(--primary)' }}>{short}</span>
                        <span style={{ width: 7, height: 7, borderRadius: '50%', background: 'var(--secondary)', flexShrink: 0 }} />
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </>
    
        )}
      </div>
    </AppLayout>
  );
}
